import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import * as crypto from "@/lib/crypto";
import { persistIdentity } from "@/lib/e2ee";
import { registerOwnSyncRequest } from "@/lib/realtime";
import { useQueryClient } from "@tanstack/react-query";

const POLL_INTERVAL_MS = 2000;

export type SyncRequestPhase = "idle" | "waiting" | "completing" | "done" | "denied" | "expired" | "error";

/**
 * Shared core of the "sync this device's identity from a trusted session"
 * flow - extracted so both the manual Security Settings button and the
 * automatic identity-choice prompt (shown when a session has no local
 * identity but the account is active elsewhere) use exactly one
 * implementation, rather than two copies that could drift.
 */
export function useSyncRequest(active: boolean) {
  const queryClient = useQueryClient();
  const [phase, setPhase] = useState<SyncRequestPhase>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const pollTimerRef = useRef<number>();
  const ephemeralRef = useRef<{ publicKey: string; privateKey: string } | null>(null);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;

    async function start() {
      setPhase("waiting");
      setErrorMessage(null);

      const ephemeral = crypto.generateIdentityKeypair();
      ephemeralRef.current = ephemeral;

      const { id } = await api.post<{ id: string; expiresAt: string }>("/api/auth/sync/request", {
        ephemeralPublicKey: ephemeral.publicKey
      });
      if (cancelled) return;
      // Prevents this same tab from being prompted to approve its own
      // request - see realtime.tsx's own dispatch check.
      registerOwnSyncRequest(id);
      poll(id);
    }

    function poll(id: string) {
      pollTimerRef.current = window.setTimeout(async () => {
        if (cancelled) return;
        try {
          const result = await api.get<
            | { status: "pending" }
            | { status: "denied" }
            | { status: "expired" }
            | { status: "approved"; sealedIdentityPayload: string }
          >(`/api/auth/sync/${id}/poll`);
          if (cancelled) return;

          if (result.status === "pending") {
            poll(id);
            return;
          }
          if (result.status === "expired") {
            setPhase("expired");
            return;
          }
          if (result.status === "denied") {
            setPhase("denied");
            return;
          }

          setPhase("completing");
          const ephemeral = ephemeralRef.current!;
          const identity = crypto.unsealIdentityFromTrustedDevice(
            result.sealedIdentityPayload,
            ephemeral.publicKey,
            ephemeral.privateKey
          );
          await persistIdentity(identity);
          // Narrower than an earlier version of this flow, which also
          // invalidated a bare ["messages"] key - broader than this
          // codebase's own convention (["messages", conversationId]) and not
          // needed, since individual conversations refetch their own
          // messages naturally when opened.
          queryClient.invalidateQueries({ queryKey: ["conversations"] });
          setPhase("done");
        } catch {
          if (!cancelled) {
            setPhase("error");
            setErrorMessage("Something went wrong finishing this sync. You can try again.");
          }
        }
      }, POLL_INTERVAL_MS);
    }

    start().catch(() => {
      if (!cancelled) {
        setPhase("error");
        setErrorMessage("Couldn't start a sync request. Check your connection and try again.");
      }
    });

    return () => {
      cancelled = true;
      window.clearTimeout(pollTimerRef.current);
    };
  }, [active, queryClient]);

  function reset() {
    setPhase("idle");
    window.clearTimeout(pollTimerRef.current);
  }

  return { phase, errorMessage, reset };
}
